import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  useTheme,
} from '@mui/material';
import DashboardTabs from 'components/DashboardTabs';
import CellRulesDataGrid from 'components/Pages/OperationsSettings/CellRulesDataGrid';
import CellRulePanelDropdown from 'components/Pages/OperationsSettings/CellRulesDataGrid/CellRulePanel/CellRulePanelDropdown';
import { useStainTypesUsedByPanel } from 'components/Pages/OperationsSettings/CellRulesDataGrid/panel.util';
import { ShapeType } from 'components/Procedure/Header/SlideInteractionMenu/SlideAnnotationTools/options';
import { TodoOption } from 'interfaces/annotation';
import { CellRuleValue, MinimalCellRule } from 'interfaces/cellRule';
import { compact, concat, filter, find, forEach, includes, indexOf, map, orderBy, some, uniq } from 'lodash';
import React, { useMemo, useState } from 'react';
import { convertOptionsWithRule2ToMultipleOptions, getTodoClassKey } from 'services/annotations/helpers';
import useCellRulePanels from 'utils/queryHooks/cellRule/useCellRulePanels';
import useCellRules from 'utils/queryHooks/cellRule/useCellRules';
import { useCurrentUser } from 'utils/useCurrentUser';
import ClassesToAnnotateList, { ClassesToAnnotateListSkeleton } from '../ClassesToAnnotateList';

interface PresetsTodoTypesProps {
  selectedClassesToAnnotate: TodoOption[];
  onClassesSelected: (classes: TodoOption[]) => void;
  studyId: string;
  getClassToAnnotateFromTaxonomyId: (taxonomyId: string) => TodoOption;
  isLoading?: boolean;
  isError?: boolean;
  selectedPanelId: string | null;
  setSelectedPanelId: (panelId: string | null) => void;
}

const CellRulesTab: React.FC<PresetsTodoTypesProps> = ({
  selectedClassesToAnnotate,
  onClassesSelected,
  studyId,
  getClassToAnnotateFromTaxonomyId,
  isLoading,
  isError,
  selectedPanelId,
  setSelectedPanelId,
}) => {
  const theme = useTheme();
  const subTabs = ['Rules', 'Phenotypic markers', 'Lineage markers'];
  const [activeTab, setActiveTab] = useState<string>('Rules');
  const activeTabIndex = indexOf(subTabs, activeTab) !== -1 ? indexOf(subTabs, activeTab) : 0;

  const selectedClassesToAnnotateNames = useMemo(
    () => compact(map(selectedClassesToAnnotate, (item) => getTodoClassKey(item))) ?? [],
    [selectedClassesToAnnotate]
  );

  const { data: panels, isLoading: isLoadingPanels, isError: isErrorLoadingPanels } = useCellRulePanels(studyId);

  const {
    data: panel,
    isLoading: isLoadingPanel,
    isError: isErrorLoadingPanel,
  } = useCellRules(selectedPanelId, studyId);

  const savedStainTypeIds = uniq(panel?.stainTypeIds);
  const savedCellTypeIds = uniq(map(panel?.rules, 'cellTypeId'));

  const [selectCellRulesCombinationsDialogOpen, setSelectCellRulesCombinationsDialogOpen] = useState(false);
  const [annotationsRulesClassesOptions, setAnnotationsRulesClassesOptions] = useState<TodoOption[]>([]);
  const [isCompact, setIsCompact] = useState(false);
  const [selectedStainTypeIds, setStainTypeIdsToShow] = useState(null);
  const [selectedCellTypeIds, setCellTypeIdsToShow] = useState(null);
  const [selectedRules, setSelectedRules] = useState<MinimalCellRule[]>([]);

  const stainTypeIdsToShow = selectedStainTypeIds ?? savedStainTypeIds;
  const cellTypeIdsToShow = selectedCellTypeIds ?? savedCellTypeIds;
  const panelStainTypes = useStainTypesUsedByPanel(panel);
  const currentUserProfile = useCurrentUser();

  const phenotypicMarkerTodos: TodoOption[] = useMemo(() => {
    return map(filter(panelStainTypes, { phenotypic: true }), (stainType) => ({
      name: stainType.id,
      displayName: stainType.displayName,
      color: theme.palette.primary.main,
      className: 'point',
      shapeType: ShapeType.Point,
      isMarkerAnnotation: true,
    }));
  }, [panelStainTypes]);

  const lineageMarkerTodos: TodoOption[] = useMemo(() => {
    return map(filter(panelStainTypes, { lineage: true }), (stainType) => ({
      name: stainType.id,
      displayName: stainType.displayName,
      color: theme.palette.primary.main,
      className: 'point',
      shapeType: ShapeType.Point,
      isMarkerAnnotation: true,
    }));
  }, [panelStainTypes]);

  const addClassesToAssignment = (classesToAnnotate: TodoOption[]) => {
    const newClassesToAnnotate = filter(classesToAnnotate, (todo) => {
      return !includes(selectedClassesToAnnotateNames, getTodoClassKey(todo));
    });

    const finalSelected = orderBy(concat(selectedClassesToAnnotate, newClassesToAnnotate), (newClassToAnnotate) =>
      indexOf(selectedClassesToAnnotateNames, getTodoClassKey(newClassToAnnotate))
    );

    onClassesSelected(finalSelected);
  };

  const onSelectionFinished = () => {
    const todosFromSelectedCellRules: TodoOption[] = [];

    forEach(selectedRules, (cellRule) => {
      if (cellRule.ruleValue !== CellRuleValue.Indifferent) {
        let taxonomyToAnnotate: TodoOption = find(todosFromSelectedCellRules, { name: cellRule.cellTypeId });
        if (!taxonomyToAnnotate) {
          taxonomyToAnnotate = getClassToAnnotateFromTaxonomyId(cellRule.cellTypeId);
          taxonomyToAnnotate.markerPositivity = {};
          taxonomyToAnnotate.createdAt = new Date();
          taxonomyToAnnotate.createdBy = currentUserProfile.userId;
          taxonomyToAnnotate.originPanelId = selectedPanelId;
          todosFromSelectedCellRules.push(taxonomyToAnnotate);
        }
        taxonomyToAnnotate.markerPositivity[cellRule.stainTypeId] = cellRule.ruleValue;
      }
    });

    if (some(selectedRules, (rule) => rule.ruleValue === CellRuleValue.OneOfTwoMustBePositive)) {
      setSelectCellRulesCombinationsDialogOpen(true);
      setAnnotationsRulesClassesOptions(convertOptionsWithRule2ToMultipleOptions(todosFromSelectedCellRules));
      return;
    }

    const phenotypicSelectedClassesToAnnotate = filter(selectedClassesToAnnotate, (todo) => {
      return includes(map(phenotypicMarkerTodos, 'name'), todo.name);
    });

    const addedTodos = concat(todosFromSelectedCellRules, phenotypicSelectedClassesToAnnotate);

    addClassesToAssignment(addedTodos);
  };

  const handleShowCompactToggle = (isCompacting: boolean) => {
    setIsCompact(isCompacting);

    if (isCompacting) {
      const selectedRulesStainTypeIds = uniq(map(selectedRules, 'stainTypeId'));
      const selectedRulesCellTypeIds = uniq(map(selectedRules, 'cellTypeId'));
      setStainTypeIdsToShow(
        orderBy(selectedRulesStainTypeIds, (stainTypeId) => indexOf(savedStainTypeIds, stainTypeId))
      );
      setCellTypeIdsToShow(orderBy(selectedRulesCellTypeIds, (cellTypeId) => indexOf(savedCellTypeIds, cellTypeId)));
    } else {
      setStainTypeIdsToShow(savedStainTypeIds);
      setCellTypeIdsToShow(savedCellTypeIds);
    }
  };

  const border = `1px solid ${theme.palette.mode == 'light' ? 'lightgrey' : 'rgb(81, 81, 81)'}`;

  return (
    <Grid item container direction="column" mt={2}>
      <Grid item px={2}>
        <CellRulePanelDropdown
          panels={panels ?? []}
          selectedPanelId={selectedPanelId}
          onSelectPanel={setSelectedPanelId}
          isLoading={isLoadingPanels || isLoading}
          studyId={studyId}
          textFieldProps={{
            error: isErrorLoadingPanels || isError,
            helperText: isErrorLoadingPanels || isError ? 'Error loading panels' : undefined,
          }}
        />
      </Grid>
      <Grid item container direction="column" xs={true} sx={{ overflow: 'auto' }}>
        <Grid
          item
          sx={{
            px: 1,
            borderRadius: 0,
            borderBottom: border,
          }}
        >
          <DashboardTabs
            tabsDisplayTexts={subTabs}
            tabs={subTabs}
            changeTab={setActiveTab}
            activeTabIndex={activeTabIndex}
          />
        </Grid>
        <Grid item xs={true} width="100%" p={1} sx={{ overflow: 'auto' }}>
          {activeTab === 'Rules' &&
            (selectedPanelId && isErrorLoadingPanel ? (
              <Typography color="error">Error loading panel rules</Typography>
            ) : (
              <>
                <CellRulesDataGrid
                  panelId={selectedPanelId}
                  stainTypeIds={stainTypeIdsToShow ?? savedStainTypeIds}
                  cellTypeIds={cellTypeIdsToShow ?? savedCellTypeIds}
                  rules={panel?.rules}
                  isLoading={isLoadingPanel}
                  selectedCells={selectedRules}
                  onSelectCellRules={setSelectedRules}
                  onSelectionFinished={onSelectionFinished}
                  isCompact={isCompact}
                  handleShowCompactToggle={handleShowCompactToggle}
                />
                {selectCellRulesCombinationsDialogOpen && (
                  <Dialog
                    open={selectCellRulesCombinationsDialogOpen}
                    onClose={() => setSelectCellRulesCombinationsDialogOpen(false)}
                    maxWidth="md"
                    fullWidth
                  >
                    <DialogTitle>Select Cell Rules Combinations</DialogTitle>
                    <DialogContent>
                      <Typography variant="body1" gutterBottom>
                        Please select the cell rules combinations you want to apply.
                      </Typography>
                      <ClassesToAnnotateList
                        classesToAnnotate={annotationsRulesClassesOptions}
                        isSelectable
                        selectProps={{
                          isTodoSelected: (id) => includes(selectedClassesToAnnotateNames, id),
                          selectedClassesToAnnotate,
                          onClassesSelected,
                        }}
                      />
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={() => setSelectCellRulesCombinationsDialogOpen(false)} color="primary">
                        Finish
                      </Button>
                    </DialogActions>
                  </Dialog>
                )}
              </>
            ))}
          {activeTab === 'Phenotypic markers' &&
            (selectedPanelId && isLoadingPanel ? (
              <ClassesToAnnotateListSkeleton />
            ) : isErrorLoadingPanel ? (
              <Typography color="error">Error loading panel rules</Typography>
            ) : (
              <ClassesToAnnotateList
                classesToAnnotate={phenotypicMarkerTodos}
                isSelectable
                selectProps={{
                  isTodoSelected: (id) => includes(selectedClassesToAnnotateNames, id),
                  selectedClassesToAnnotate,
                  onClassesSelected,
                }}
              />
            ))}
          {activeTab === 'Lineage markers' &&
            (selectedPanelId && isLoadingPanel ? (
              <ClassesToAnnotateListSkeleton />
            ) : isErrorLoadingPanel ? (
              <Typography color="error">Error loading panel rules</Typography>
            ) : (
              <Grid container direction="column" sx={{ height: '100%' }}>
                <Grid item>
                  <Alert severity="warning">
                    This tab should be used mostly for markers that appear in cell rules as 2.
                  </Alert>
                </Grid>
                <Grid item xs={true} sx={{ overflow: 'auto', my: 1 }}>
                  <ClassesToAnnotateList
                    classesToAnnotate={lineageMarkerTodos}
                    isSelectable
                    selectProps={{
                      isTodoSelected: (id) => includes(selectedClassesToAnnotateNames, id),
                      selectedClassesToAnnotate,
                      onClassesSelected,
                    }}
                  />
                </Grid>
              </Grid>
            ))}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default CellRulesTab;
